import React, { useRef, useState } from 'react';
import { CloseButton, Modal, Select } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faGift, faListCheck, faPercent, faPlus } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button } from '../../../../components/Button';
import { SectionCard } from '../../shared/SectionCard/SectionCard';
import { AdminApprovalRequestDetailView, AdminApprovalRequestView } from '../../../../services/admin-approval-requests/admin-approval-requests.service';
import { EmployeeView } from '../../../../services/employees/employees.service';
import { ApprovalMode, SectionKey } from '../../shared/types/dashboard-page.types';
import { asCurrency } from '../../shared/utils/dashboard-page.utils';
import { ApprovalExportRow, downloadApprovalRequestsExcel, printApprovalRequestsPdf } from './approval-requests-export.utils';
import classes from './approval-requests-section.module.scss';

const ACTION_LABELS: Record<string, string> = {
  global_discount: 'הנחה (כללית)',
  item_discount: 'הנחה לפריטים',
  cancel: 'ביטול',
  on_the_house: 'על חשבון הבית',
  general_item: 'פריט כללי',
};

const ACTION_ICONS: Record<string, IconProp> = {
  global_discount: faPercent as IconProp,
  item_discount: faListCheck as IconProp,
  cancel: faBan as IconProp,
  on_the_house: faGift as IconProp,
  general_item: faPlus as IconProp,
};

const REASON_LABELS: Record<string, string> = {
  cancel_order: 'ביטול הזמנה',
  vip: 'VIP',
  waiter_error: 'טעות במנה',
  staff: 'שת"פ',
  other: 'אחר',
  manager_action: 'פעולת מנהל',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'ממתין',
  approved: 'מאושר',
  denied: 'נדחה',
};

const formatRequestDateTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const pad = (part: number): string => String(part).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}, ${pad(date.getDate())}.${pad(date.getMonth() + 1)}`;
};

const formatRequestDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const pad = (part: number): string => String(part).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

const formatRequestTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const pad = (part: number): string => String(part).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getActionLabel = (row: AdminApprovalRequestDetailView): string => {
  if (row.payload.actionType === 'cancel' && row.payload.cancelScope === 'order') {
    return 'ביטול כל ההזמנה';
  }
  return ACTION_LABELS[row.actionType] ?? row.actionType;
};

const getRequestDetail = (row: AdminApprovalRequestDetailView): string => {
  switch (row.payload.actionType) {
    case 'global_discount':
      return row.payload.discountMode === 'percentage'
        ? `${row.payload.discountInput}% — הנחה על כל ההזמנה, כולל מוצרים שייתווספו בהמשך`
        : asCurrency(row.payload.discountInput);
    case 'item_discount':
    case 'on_the_house': {
      const total = row.payload.items.reduce((sum, item) => sum + item.discountInput, 0);
      return asCurrency(total);
    }
    case 'cancel': {
      if (row.payload.cancelScope === 'order') {
        return 'ביטול כל ההזמנה';
      }
      const orderlineById = new Map(row.orderlines.map((line) => [line.orderlineId, line]));
      const total = row.payload.items.reduce((sum, item) => {
        const unitPrice = orderlineById.get(item.orderlineId)?.unitPrice ?? 0;
        return sum + unitPrice * item.quantity;
      }, 0);
      return asCurrency(total);
    }
    case 'general_item':
      return `${row.payload.quantity} × ${asCurrency(row.payload.unitPrice)}`;
    default:
      return '';
  }
};

const getVisibleOrderlines = (row: AdminApprovalRequestDetailView): AdminApprovalRequestDetailView['orderlines'] => {
  const orderlineById = new Map(row.orderlines.map((line) => [line.orderlineId, line]));

  switch (row.payload.actionType) {
    case 'item_discount':
    case 'on_the_house':
    case 'cancel':
      return row.payload.items.map((item) => {
        const orderline = orderlineById.get(item.orderlineId);
        return {
          orderlineId: item.orderlineId,
          productName: orderline?.productName ?? null,
          quantity: item.quantity,
          unitPrice: orderline?.unitPrice ?? 0,
        };
      });
    case 'general_item':
      return [{
        orderlineId: -row.id,
        productName: 'פריט כללי',
        quantity: row.payload.quantity,
        unitPrice: row.payload.unitPrice,
      }];
    case 'global_discount':
    default:
      return row.orderlines;
  }
};

export type ApprovalRequestsSectionProps = {
  collapsedSections: Record<SectionKey, boolean>;
  toggleSection: (section: SectionKey) => void;
  approvalMode: ApprovalMode;
  setApprovalMode: (mode: ApprovalMode) => void;
  loadMoreApprovalRows: () => void;
  approvalRows: { rows: AdminApprovalRequestView[]; hasMore: boolean; totalRows: number };
  approvalDetailsById: Record<number, AdminApprovalRequestDetailView>;
  managerOptions: EmployeeView[];
  selectedApproverName: string | null;
  setSelectedApproverName: (name: string | null) => void;
  approvalsActionBusy: boolean;
  approvalsActionError: string | null;
  runApprovalDecision: (requestId: number, decision: 'approved' | 'denied') => Promise<'ok' | 'already-decided' | 'error'>;
  approvalScopeDateText: string;
  loadApprovalExportSource: () => Promise<{ rows: AdminApprovalRequestView[]; detailsById: Record<number, AdminApprovalRequestDetailView> }>;
  approvalContextLabel?: string | null;
};

export const ApprovalRequestsSection: React.FC<ApprovalRequestsSectionProps> = ({
  collapsedSections,
  toggleSection,
  approvalMode,
  setApprovalMode,
  loadMoreApprovalRows,
  approvalRows,
  approvalDetailsById,
  managerOptions,
  selectedApproverName,
  setSelectedApproverName,
  approvalsActionBusy,
  approvalsActionError,
  runApprovalDecision,
  approvalScopeDateText,
  loadApprovalExportSource,
  approvalContextLabel,
}) => {
  const [pendingDecision, setPendingDecision] = useState<{ row: AdminApprovalRequestView; decision: 'approved' | 'denied' } | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const exportInFlightRef = useRef(false);

  const handleListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 80;
    if (nearBottom && approvalRows.hasMore) {
      loadMoreApprovalRows();
    }
  };

  const closePendingDecision = () => {
    setPendingDecision(null);
    setSelectedApproverName(null);
  };

  const confirmPendingDecision = async () => {
    if (!pendingDecision) {
      return;
    }
    const result = await runApprovalDecision(pendingDecision.row.id, pendingDecision.decision);
    if (result === 'already-decided') {
      notifications.show({
        color: 'yellow',
        message: 'הבקשה כבר טופלה',
        autoClose: 3000,
      });
    }
    if (result === 'ok' || result === 'already-decided') {
      closePendingDecision();
    }
  };

  const pendingDetail = pendingDecision ? approvalDetailsById[pendingDecision.row.id] : null;

  const toExportRows = (
    rows: AdminApprovalRequestView[],
    detailsById: Record<number, AdminApprovalRequestDetailView>,
  ): ApprovalExportRow[] => rows.map((row) => {
    const detail = detailsById[row.id];
    const items = detail
      ? getVisibleOrderlines(detail)
        .map((line) => `${line.quantity} x ${line.productName ?? 'פריט כללי'}`)
        .join(' | ')
      : '';

    return {
      statusKey: row.status,
      status: STATUS_LABELS[row.status] ?? row.status,
      table: detail?.tableLabel ? `שולחן ${detail.tableLabel}` : '-',
      date: formatRequestDate(row.createdAt),
      time: formatRequestTime(row.createdAt),
      type: detail ? getActionLabel(detail) : row.actionType,
      requestedBy: row.requestedByName,
      decidedBy: row.decidedByName ?? '-',
      reason: REASON_LABELS[row.reason] ?? row.reason,
      amount: detail ? getRequestDetail(detail) : '-',
      items: items || '-',
      note: detail?.note ?? '-',
    };
  });

  const runExport = async (kind: 'excel' | 'pdf') => {
    if (exportInFlightRef.current) {
      return;
    }
    exportInFlightRef.current = true;
    setExportBusy(true);
    try {
      const source = await loadApprovalExportSource();
      const rows = toExportRows(source.rows, source.detailsById);
      if (kind === 'excel') {
        downloadApprovalRequestsExcel(rows, sectionTitle);
      } else {
        printApprovalRequestsPdf(rows, sectionTitle);
      }
    } catch (error: unknown) {
      notifications.show({
        color: 'red',
        message: error instanceof Error ? error.message : 'ייצוא בקשות האישור נכשל',
        autoClose: 3000,
      });
    } finally {
      setExportBusy(false);
      exportInFlightRef.current = false;
    }
  };

  const normalizedContext = approvalContextLabel?.trim() || approvalScopeDateText?.trim() || '';
  const sectionTitle = normalizedContext
    ? `בקשות אישור - ${normalizedContext}`
    : 'בקשות אישור';

  return (
    <SectionCard title={sectionTitle} sectionKey="approvals" collapsed={collapsedSections.approvals} onToggle={() => toggleSection('approvals')}>
      <div className={classes.controlsRow}>
        <div className={classes.filtersGroup}>
          <Button variant="cancel" className={approvalMode === 'all' ? `${classes.pill} ${classes.pillActive}` : classes.pill} onClick={() => setApprovalMode('all')} dataTestId="dashboard-approvals-filter-all">הכל</Button>
          <Button variant="cancel" className={approvalMode === 'pending' ? `${classes.pill} ${classes.pillActive}` : classes.pill} onClick={() => setApprovalMode('pending')} dataTestId="dashboard-approvals-filter-pending">ממתין</Button>
          <Button variant="cancel" className={approvalMode === 'approved' ? `${classes.pill} ${classes.pillActive}` : classes.pill} onClick={() => setApprovalMode('approved')} dataTestId="dashboard-approvals-filter-approved">מאושר</Button>
          <Button variant="cancel" className={approvalMode === 'denied' ? `${classes.pill} ${classes.pillActive}` : classes.pill} onClick={() => setApprovalMode('denied')} dataTestId="dashboard-approvals-filter-denied">נדחה</Button>
        </div>
        <div className={classes.exportGroup}>
          <Button
            variant="cancel"
            className={classes.exportButton}
            disabled={exportBusy}
            onClick={() => { void runExport('pdf'); }}
            dataTestId="dashboard-approvals-export-pdf"
          >
            PDF
          </Button>
          <Button
            variant="cancel"
            className={classes.exportButton}
            disabled={exportBusy}
            onClick={() => { void runExport('excel'); }}
            dataTestId="dashboard-approvals-export-excel"
          >
            EXCEL
          </Button>
        </div>
      </div>

      <div className={classes.cardList} onScroll={handleListScroll}>
        {approvalRows.rows.map((row) => {
          const detail = approvalDetailsById[row.id];
          const visibleOrderlines = detail ? getVisibleOrderlines(detail) : [];
          return (
            <div key={row.id} className={classes.card} data-status={row.status} data-testid={`dashboard-approvals-card-${row.id}`}>
              <div className={classes.cardTop}>
                <span className={`${classes.statusGroup} ${classes.cardTopRight}`}>
                  <span className={classes.statusDot} data-status={row.status} />
                  <span className={classes.statusBadge} data-status={row.status}>{STATUS_LABELS[row.status] ?? row.status}</span>
                </span>
                {detail?.tableLabel ? <span className={`${classes.tableBadge} ${classes.cardTopCenter}`}>שולחן {detail.tableLabel}</span> : null}
                <span className={`${classes.requestTime} ${classes.cardTopLeft}`}>{formatRequestDateTime(row.createdAt)}</span>
              </div>

              {detail ? (
                <>
                  <div className={classes.metaGrid}>
                    <div className={classes.metaRequesterRow}>
                      <span className={classes.actionBadge} data-action={detail.actionType}>
                        <FontAwesomeIcon icon={ACTION_ICONS[detail.actionType]} />
                        {getActionLabel(detail)}
                      </span>
                      <span className={classes.metaRequesterText}>
                        <span className={classes.metaLabel}>בוקש ע"י</span>
                        {' '}
                        <span className={classes.metaValue}>{row.requestedByName}</span>
                        {row.status !== 'pending' && row.decidedByName ? (
                          <span className={classes.metaDeciderLine}>
                            <span className={classes.metaLabel}>{row.status === 'approved' ? 'אושר ע"י' : 'נדחה ע"י'}</span>
                            {' '}
                            <span className={classes.metaValue}>{row.decidedByName}</span>
                          </span>
                        ) : null}
                      </span>
                    </div>
                    <div className={classes.metaRow}>
                      <span className={classes.metaLabel}>סיבה</span>
                      {' '}
                      <span className={classes.metaValue}>{REASON_LABELS[row.reason] ?? row.reason}</span>
                      {row.reason === 'other' && detail.note ? (
                        <>
                          {' • '}
                          <span className={classes.metaLabel}>הערה</span>
                          {' '}
                          <span className={classes.metaValue}>{detail.note}</span>
                        </>
                      ) : null}
                    </div>
                  </div>

                  <div className={classes.amountSection}>
                    <span className={classes.amountTitle}>סכום הנחה</span>
                    {' '}
                    <span className={classes.amountBar}>{getRequestDetail(detail)}</span>
                  </div>

                  {visibleOrderlines.length > 0 ? (
                    <div className={classes.orderlines}>
                      <div className={classes.orderlinesTitle}>פריטי הזמנה</div>
                      {visibleOrderlines.map((line) => (
                        <div key={line.orderlineId} className={classes.orderlineRow}>
                          <span className={classes.orderlineQty}>{`x ${line.quantity}`}</span>
                          {' '}
                          {line.productName ?? 'פריט כללי'}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : (
                <p className={classes.loadingHint}>טוען פרטים...</p>
              )}

              {row.status === 'pending' ? (
                <div className={classes.actions}>
                  <Button
                    variant="cancel"
                    className={`${classes.decisionButton} ${classes.decisionButtonDenied}`}
                    fullWidth
                    disabled={approvalsActionBusy}
                    onClick={() => setPendingDecision({ row, decision: 'denied' })}
                    dataTestId={`dashboard-approvals-deny-${row.id}`}
                  >
                    דחה
                  </Button>
                  <Button
                    variant="confirm"
                    className={`${classes.decisionButton} ${classes.decisionButtonApproved}`}
                    fullWidth
                    disabled={approvalsActionBusy}
                    onClick={() => setPendingDecision({ row, decision: 'approved' })}
                    dataTestId={`dashboard-approvals-approve-${row.id}`}
                  >
                    אשר
                  </Button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <Modal
        opened={Boolean(pendingDecision)}
        onClose={() => {
          if (!approvalsActionBusy) {
            closePendingDecision();
          }
        }}
        centered
        withCloseButton={false}
        closeOnClickOutside={!approvalsActionBusy}
        closeOnEscape={!approvalsActionBusy}
        classNames={{ content: classes.confirmModalContent, body: classes.confirmModalBody }}
      >
        {pendingDecision ? (
          <div className={classes.confirmModal} data-decision={pendingDecision.decision}>
            <CloseButton
              className={classes.confirmModalClose}
              disabled={approvalsActionBusy}
              onClick={closePendingDecision}
              data-testid="dashboard-approvals-confirm-close"
            />
            <span className={classes.confirmIcon} data-decision={pendingDecision.decision}>
              {pendingDecision.decision === 'approved' ? '✓' : '✕'}
            </span>
            <h3 className={classes.confirmTitle}>
              {pendingDecision.decision === 'approved' ? 'אישור בקשה' : 'דחיית בקשה'}
            </h3>
            <p className={classes.confirmSubtitle}>נא לבחור את שם המאשר ל{pendingDecision.decision === 'approved' ? 'אישור' : 'דחיית'} הבקשה</p>

            <div className={classes.confirmSummaryLabel}>בקשה</div>
            <div className={classes.confirmSummaryCard}>
              <span className={classes.statusDot} data-status={pendingDecision.row.status} />
              {pendingDetail ? [
                getActionLabel(pendingDetail),
                pendingDetail.tableLabel ? `שולחן ${pendingDetail.tableLabel}` : null,
                pendingDecision.row.requestedByName,
              ].filter(Boolean).join(' · ') : pendingDecision.row.requestedByName}
            </div>

            <div className={classes.confirmApproverField}>
              <div className={classes.confirmApproverLabel}>שם המאשר</div>
              <Select
                placeholder="בחר שם..."
                data={managerOptions.map((manager) => ({ value: manager.fullName, label: manager.fullName }))}
                value={selectedApproverName}
                onChange={setSelectedApproverName}
                searchable
                data-testid="dashboard-approvals-approver-select"
                classNames={{ input: classes.approverInput, dropdown: classes.approverDropdown, option: classes.approverOption }}
              />
              <p className={classes.confirmHint}>השם יירשם ביומן הפעולות</p>
            </div>

            {approvalsActionError ? <p className={classes.actionError}>{approvalsActionError}</p> : null}

            <div className={classes.confirmActions}>
              <Button
                variant="cancel"
                className={classes.decisionButton}
                fullWidth
                disabled={approvalsActionBusy}
                onClick={closePendingDecision}
                dataTestId="dashboard-approvals-confirm-cancel"
              >
                ביטול
              </Button>
              <Button
                variant={pendingDecision.decision === 'approved' ? 'confirm' : 'cancel'}
                className={`${classes.decisionButton} ${pendingDecision.decision === 'approved' ? classes.decisionButtonApproved : classes.decisionButtonDenied}`}
                fullWidth
                disabled={approvalsActionBusy || !selectedApproverName}
                loading={approvalsActionBusy}
                onClick={() => { void confirmPendingDecision(); }}
                dataTestId="dashboard-approvals-confirm-submit"
              >
                {pendingDecision.decision === 'approved' ? 'אשר בקשה' : 'דחה בקשה'}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </SectionCard>
  );
};
